import React, { useState } from 'react';
import {
  Heading,
  ListItem,
  Stack,
  Tag,
  TagText,
  Text,
} from '@alefeducation/ui-elements-library-core';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { MemberActions } from './MemberActions';
import { columnWidths } from './MemberColHeader';
import { Member, MembershipV2, MembershipV2Role } from '../../apollo/queries';
import { Status } from '../Organisations';

interface Props {
  members: Member[];
  orgCode: string;
  pageNumber: Number;
  hasUpdatePermission: boolean;
  hasAssignPermission: boolean;

  onRemove(memberId: number): void;
}

const StyleListItem = styled(ListItem)<{ showCursor?: boolean }>`
  padding: ${({ theme: { space } }: any) =>
    `${space[24]}px ${space[32]}px ${space[24]}px ${space[24]}px`};

  ${columnWidths};

  .action-icons {
    visibility: hidden;
    align-items: center;
  }

  :hover {
    cursor: ${({ showCursor }) => (showCursor ? 'pointer' : 'auto')};

    .action-container,
    .action-icons {
      visibility: visible;
    }
  }
`;

const StyledTag = styled(Tag)`
  background-color: ${({ theme }: any) => theme.colors.subjects.english[30]};
  color: ${({ theme }: any) => theme.colors.tag};
  margin: 8px;
`;

const StyledLink = styled.div``;

const MemberRows = ({
  members,
  orgCode,
  onRemove,
  pageNumber,
  hasUpdatePermission,
  hasAssignPermission,
}: Props) => {
  const history = useHistory();

  const getV2Roles = (memberships: MembershipV2[]) => {
    const membership = memberships.find((m) => m.organisation === orgCode);
    return membership
      ? membership.rolesWithName.map((role: MembershipV2Role) => role.name)
      : [];
  };

  const activeRows = () => {
    const activeMemberships: number[] = [];
    members.forEach((member, index) => {
      if (member.enabled) {
        activeMemberships.push(index);
      }
    });
    return activeMemberships;
  };

  const shouldRenderActions = () => hasAssignPermission;

  return (
    <>
      {members.map((member, index) => {
        const { lastName, firstName, id, membershipsV2, email } = member;

        return (
          <div key={member.id}>
            <StyledLink
              data-testid={`member-row-${id}`}
              onClick={() => {
                if (hasUpdatePermission)
                  history.push(`/organisations/${orgCode}/members/${id}/edit`, {
                    pageNumber,
                  });
              }}
            >
              <StyleListItem
                key={`org-row-${index}`}
                data-testid={id}
                showCursor={hasUpdatePermission}
              >
                <Stack
                  className="member-fname"
                  flexDirection="column"
                  justifyContent="center"
                  data-testid={`org-row-fname-${index}`}
                  key={`firstName-${firstName}`}
                >
                  <Heading variant="heading-small">{firstName || ''}</Heading>
                </Stack>

                <Stack
                  className="member-lname"
                  flexDirection="column"
                  justifyContent="center"
                  data-testid={`org-row-lname-${index}`}
                  key={`lastName-${lastName}`}
                >
                  <Heading variant="heading-small">{lastName || ''}</Heading>
                </Stack>

                <Stack
                  className="member-email"
                  flexDirection="column"
                  justifyContent="center"
                  key={id}
                >
                  <Text variant="body-large" color="primary.100" weight="light">
                    {email}
                  </Text>
                </Stack>

                <Stack
                  className="member-roles"
                  flexDirection="column"
                  justifyContent="left"
                  key={`roles-${index}`}
                >
                  {getV2Roles(membershipsV2).map((role, roleIndex) => {
                    return (
                      <StyledTag
                        key={`member-roles-${id}${roleIndex}-${role}`}
                        padding="thick"
                      >
                        <TagText>{role}</TagText>
                      </StyledTag>
                    );
                  })}
                </Stack>
                <Stack
                  className="status-container"
                  flexDirection="column"
                  justifyContent="center"
                  key={`isActive-${index}`}
                >
                  <Status
                    isActive={activeRows().includes(index)}
                    key={`status-${index}`}
                  />
                </Stack>
                <Stack className="action-icons">
                  {shouldRenderActions() && (
                    <MemberActions
                      hasAssignPermission={hasAssignPermission}
                      onDeleteClick={() => onRemove(parseInt(id))}
                      memberId={parseInt(id)}
                    />
                  )}
                </Stack>
              </StyleListItem>
            </StyledLink>
          </div>
        );
      })}
    </>
  );
};

export default MemberRows;
